import { ReebeloConditions } from '@lambda/reebelo';
import settings from '@/settings';
import i18n from '@/settings/i18n';
import {
  formatWarrantyInfo,
  getWarrantyInfoByCategoryPks,
  normalizeWarrantyLength,
} from './warranty';

// Translates the warranty duration's unit (e.g. "12 Months" -> "12 mois")
// while keeping the English display unchanged, then builds the "X Warranty"
// label (French reorders to "Garantie X"). `i18n.init` is called lazily inside
// the function (not at module scope) so this lib stays importable in node tests,
// where the build-time loader that rewrites `init` doesn't run.
const buildWarrantyLabel = (formatted: string): string => {
  const t = i18n.init('cart');

  const localizeDuration = (): string => {
    if (i18n.getLocale() === 'en') {
      return formatted;
    }

    const match = formatted.match(/^(up to\s+)?(\d+)\s+(month|year|day)s?$/i);

    if (!match) {
      return formatted;
    }

    const [, upTo, count, unitRaw] = match;
    const isPlural = Number(count) > 1;
    const unit = unitRaw.toLowerCase();

    const unitLabel = (() => {
      if (unit === 'year') {
        return isPlural ? t`years` : t`year`;
      }

      if (unit === 'day') {
        return isPlural ? t`days` : t`day`;
      }

      return isPlural ? t`months` : t`month`;
    })();

    const duration = `${count} ${unitLabel}`;

    return upTo ? `${t`Up to`} ${duration}` : duration;
  };

  return t`${{ warranty: localizeDuration() }} Warranty`;
};

export const generateCartWarrantyText = async ({
  conditions,
  categoryPks,
  freeReturnDays,
}: {
  conditions: string[];
  categoryPks: string[];
  freeReturnDays: number;
}) => {
  const checkItemCondition = (
    streamlinedCondition: string,
    method: 'some' | 'every' = 'some',
  ) => conditions[method]((condition) => condition === streamlinedCondition);

  const checkCategoryPks = async () => {
    const warrantyInfoArray = await getWarrantyInfoByCategoryPks(categoryPks);

    if (!warrantyInfoArray?.length) {
      return {
        warrantyLength: settings.warranty_month,
        warrantyUnit: 'Month',
        eligible: true,
      };
    }
    const ineligibleWarranties = warrantyInfoArray.filter(
      (warrantyInfo) => !warrantyInfo?.eligible,
    );

    const eligibleWarranties = warrantyInfoArray.filter(
      (warrantyInfo) => warrantyInfo?.eligible,
    );

    if (ineligibleWarranties.length > 0 && eligibleWarranties.length > 0) {
      return {
        warrantyLength: settings.warranty_month,
        warrantyUnit: 'Month',
        eligible: false,
        mixedCart: true,
      };
    }

    if (eligibleWarranties.length === 0) {
      return {
        warrantyLength: settings.warranty_month,
        warrantyUnit: 'month',
        eligible: true,
      };
    }

    const firstWarranty = eligibleWarranties[0];
    const allSame = eligibleWarranties.every(
      (warranty) =>
        warranty.warrantyLength === firstWarranty.warrantyLength &&
        warranty.warrantyUnit === firstWarranty.warrantyUnit,
    );

    if (allSame) {
      // If all warranties are the same, return the warranty length and unit
      return {
        warrantyLength: firstWarranty.warrantyLength,
        warrantyUnit: firstWarranty.warrantyUnit,
        eligible: true,
      };
    }
    // If they differ, find the longest warranty in terms of days
    const longestWarranty = eligibleWarranties.reduce((longest, current) => {
      const longestInDays = normalizeWarrantyLength(
        longest?.warrantyLength,
        longest.warrantyUnit,
      );
      const currentInDays = normalizeWarrantyLength(
        current?.warrantyLength,
        current.warrantyUnit,
      );

      return currentInDays > longestInDays ? current : longest;
    });

    return {
      warrantyLength: longestWarranty.warrantyLength,
      warrantyUnit: longestWarranty.warrantyUnit,
      eligible: true,
      mixedCart: eligibleWarranties.length > 1,
    };
  };

  const allItemsAreBrandNew = checkItemCondition(
    ReebeloConditions.BrandNew,
    'every',
  );

  if (allItemsAreBrandNew) {
    return buildWarrantyLabel('12 Months');
  }

  if (['quista'].includes(settings.store)) {
    return buildWarrantyLabel('3 Months');
  }

  const warrantyInfo = await checkCategoryPks();

  const warrantyLengthInDays = normalizeWarrantyLength(
    warrantyInfo.warrantyLength,
    warrantyInfo.warrantyUnit,
  );

  if (warrantyLengthInDays <= freeReturnDays) {
    return '';
  }

  const formattedWarrantyInfo = formatWarrantyInfo(
    warrantyInfo,
    warrantyInfo.mixedCart,
  );

  if (formattedWarrantyInfo) {
    return buildWarrantyLabel(formattedWarrantyInfo);
  }

  return buildWarrantyLabel(`${settings.warranty_month} Months`);
};
